import type { Dict } from '../types'

/** German. Translated best-effort — please proofread. */
export const de: Dict = {
  languageName: 'Deutsch',

  common: {
    points: 'Punkte',
  },

  // Subject-word lexicon (see en.ts). German defaults for clothing (Kleidung = feminine), given in the
  // ACCUSATIVE object form our copy uses. A brand supplies the full phrase per form and case (e.g.
  // Schmuck = masculine → noun 'Schmuck', nounPoss 'deinen Schmuck', nounDef 'den Schmuck'). German
  // case/relative-pronoun agreement means the terms section is often best fully overridden via
  // tradeInConfig.dictionary rather than tokens.
  //
  // For the same reason the German copy still says "Wertgutschein" literally where da/en interpolate
  // {reward}: all 33 of those carry a case-marked article agreeing with a MASCULINE noun (der/den/
  // dein/deinen/einen), so a token would only be safe for a brand whose word is also masculine. The
  // reward forms below exist so the lexicon shape matches, and so a German brand can still override
  // them — but converting the copy needs four case forms, not one. Nominative given.
  subject: {
    noun: 'Kleidung',
    nounPoss: 'deine Kleidung',
    nounDef: 'die Kleidung',
    reward: 'Belohnung',
    rewardPoss: 'deine Belohnung',
    rewardDef: 'die Belohnung',
    rewardPlural: 'Belohnungen',
  },

  auth: {
    signingIn: 'Anmeldung läuft…',
    stillWorking: 'Arbeitet noch — wir holen deine früheren Bestellungen. Das kann einen Moment dauern.',
    loadingItems: 'Deine Artikel werden geladen…',
    errorTitle: 'Wir konnten deinen Trade-in nicht starten',
    errorGeneric: 'Bei der Anmeldung ist etwas schiefgelaufen.',
    missingShop: 'App ist falsch konfiguriert — shopId fehlt.',
    needSignIn: 'Du musst dich anmelden, bevor du eintauschen kannst.',
    signInFailed: 'Anmeldung fehlgeschlagen. Bitte versuche es erneut.',
    linkInvalid: 'Dieser Anmeldelink funktioniert nicht mehr. Das zugehörige Profil wurde möglicherweise gelöscht. Gehe zurück zur Startseite, um dich erneut anzumelden.',
    backToFrontPage: 'Zurück zur Startseite',
  },

  config: {
    title: 'Konfigurationsfehler',
    missingShop: 'Diesem Trade-in fehlt die Shop-Konfiguration (shopId). Bitte kontaktiere den Shop.',
  },

  login: {
    title: 'Erwecke {nounPoss} zu neuem Leben',
    subtitle: 'Melde dich an, um deinen Trade-in zu starten und ungetragene Stücke in einen Wertgutschein zu verwandeln.',
    emailLabel: 'E-Mail',
    emailPlaceholder: 'du@beispiel.de',
    start: 'Weiter',
    working: 'Bitte warten…',
    emailSentTitle: 'Prüfe deine E-Mails',
    emailSentBody: 'Wir haben einen Anmeldelink an {email} gesendet. Öffne ihn auf diesem Gerät, um fortzufahren.',
    phoneLabel: 'Telefonnummer',
    phonePlaceholder: '151 23456789',
    sendCode: 'Code senden',
    codeLabel: 'Bestätigungscode',
    codePlaceholder: '6-stelliger Code',
    verify: 'Bestätigen & fortfahren',
    changePhone: 'Andere Nummer verwenden',
    back: 'Zurück',
    methodSms: 'Mit SMS anmelden',
    methodSmsDesc: 'Wir senden dir einen Code per SMS — sofern deine Nummer bei uns hinterlegt ist.',
    methodEmail: 'Mit E-Mail anmelden',
    methodEmailDesc: 'Wir senden einen Login-Link an dein Postfach.',
    or: 'oder',
    wholesale: 'Im Laden gekauft oder geschenkt?',
    emailInvalid: 'Gib eine gültige E-Mail-Adresse ein.',
    phoneInvalid: 'Gib eine gültige Telefonnummer ein.',
    codeInvalid: 'Der Code hat nicht funktioniert. Versuche es erneut.',
    failed: 'Etwas ist schiefgelaufen. Bitte versuche es erneut.',
    step1Title: 'Finde deine Stücke',
    step1Body: 'Suche oder fotografiere, was du nicht mehr trägst.',
    step2Title: 'Sende sie ein',
    step2Body: 'Packe alles in ein Paket mit deinem vorausbezahlten Etikett.',
    step3Title: 'Erhalte deinen Wertgutschein',
    step3Body: {
      received: 'Erhalte deinen Wertgutschein, sobald wir deine Artikel erhalten haben.',
      handin: 'Erhalte deinen Wertgutschein, sobald dein Paket an der Abgabestelle gescannt wurde.',
      instant: 'Erhalte deinen Wertgutschein sofort — und sende uns anschließend deine Artikel.',
    },
  },

  country: {
    title: 'Dein Land wird möglicherweise nicht unterstützt',
    body: 'Es sieht so aus, als hätten wir möglicherweise keinen Versanddienstleister, der einen Trade-in aus deinem Land an unser Lager senden kann. Du kannst gerne fortfahren, aber wir können die Abholung womöglich nicht abschließen.',
    dismiss: 'Trotzdem fortfahren',
  },

  menu: {
    open: 'Menü',
    language: 'Sprache',
    myTradeIns: 'Meine Trade-ins',
    howItWorks: 'So funktioniert’s',
    noneYet: 'Noch keine',
    logout: 'Abmelden',
    soon: 'Bald',
  },

  // Wertgutschein-Zeilen, wiederverwendet von der "Meine Trade-ins"-Seite für die Gutschein-Sektion.
  rewards: {
    activeHeading: 'Verfügbar',
    spentHeading: 'Eingelöst',
    expires: 'Gültig bis {date}',
    active: 'Aktiv',
    spentBadge: 'Eingelöst',
  },
  // "Meine Trade-ins"-Kontoseite: Gutscheine zuerst, dann die Trade-in-Historie + Detaildialog.
  myTradeIns: {
    title: 'Meine Trade-ins',
    subtitle: 'Deine Trade-ins und Wertgutscheine.',
    back: 'Zurück zum Trade-in',
    loading: 'Deine Trade-ins werden geladen…',
    empty: 'Du hast noch keine Trade-ins oder Wertgutscheine.',
    historyHeading: 'Deine Trade-ins',
    tradeInPrefix: 'Trade-in',
    itemsCount: '{count} Artikel',
    statusReceived: 'Erhalten',
    statusOnItsWay: 'Auf dem Weg zu uns',
    statusAwaiting: 'Warten auf dein Paket',
    detailTitle: 'Trade-in-Details',
    nextHeading: 'Wie geht es weiter',
    nextReceived: 'Wir haben deine Artikel erhalten — deine Belohnung ist unterwegs.',
    nextTransit: 'Dein Paket ist auf dem Weg zu uns. Wir senden dir eine E-Mail, sobald wir deine Artikel erhalten und geprüft haben.',
    nextPickup: 'Bring deine Artikel in unser Geschäft, den Rest übernehmen wir.',
    nextShip: 'Verpacke deine Artikel und sende sie uns mit den Angaben unten.',
    itemsHeading: 'Artikel in diesem Trade-in',
    labelUnavailable: 'Das Versandetikett ist nicht mehr verfügbar.',
    close: 'Schließen',
  },

  steps: {
    items: 'Artikel',
    story: 'Deine Geschichte',
    details: 'Details',
    shipping: 'Versand',
    confirm: 'Bestätigen',
  },

  rail: {
    title: 'Dein Trade-in',
    itemCount: '{count} Artikel',
    itemCountPlural: '{count} Artikel',
    empty: 'Noch keine Artikel — füge deinen ersten hinzu und sieh den Wert wachsen.',
    loading: 'Dein Trade-in wird geladen…',
    estimatedReward: 'Gesamtwert',
    rewardNoteConfirmed: {
      received: 'Bestätigt, sobald wir deine Artikel erhalten',
      handin: 'Bestätigt, sobald du dein Paket abgibst',
      instant: 'Ausgestellt, sobald du deinen Trade-in bestätigst',
    },
    rewardNoteAdd: 'Füge Artikel hinzu, um den Wert zu sehen',
    addedManually: 'Manuell hinzugefügt',
    maxExceeded: 'Dieser Trade-in überschreitet das Maximum von {amount}. Entferne einen Artikel, um fortzufahren.',
    remove: '{name} entfernen',
    back: 'Zurück',
    continue: 'Weiter',
    confirmTradeIn: 'Trade-in bestätigen',
    done: 'Fertig',
  },

  items: {
    firstTitle: 'Finden wir deinen ersten Artikel',
    firstSubtitle: 'Mach ein Foto oder durchsuche den Katalog.',
    anotherTitle: 'Noch einen Artikel hinzufügen?',
    anotherSubtitle: 'Prüfe alles, bevor du fortfährst.',
    selectTitle: 'Artikel zum Eintauschen wählen',
    selectSubtitle: 'Aus deinen früheren Bestellungen — zum Hinzufügen tippen.',
    manualTitle: 'Artikel manuell hinzufügen',
    manualSubtitle: 'Für anderswo gekaufte Artikel.',
    estReward: 'Wert',
    bought: 'Gekauft {date}',
    addManually: 'Manuell hinzufügen',
    boughtElsewhere: 'Anderswo gekauft?',
    addedCount: '{count} hinzugefügt',
    nothingTitle: 'Nichts zum Eintauschen',
    nothingBody:
      'Wir konnten auf deinem Konto keine eintauschbaren Artikel finden. Trade-in gilt hier nur für bei uns gekaufte Artikel — sobald du eine passende Bestellung hast, erscheint sie hier.',
  },

  intro: {
    title: 'So funktioniert Trade-in',
    lead: 'Tausche {noun} von {brand} ein, die du nicht mehr trägst, und erhalte {rewardPoss} für Neues oder Secondhand.',
    step1Title: 'Finde deine Teile',
    step1Body: 'Mach ein Foto von dem, was du nicht mehr trägst, oder durchsuche unseren Katalog. Füge so viele Teile hinzu, wie du möchtest.',
    step2Title: 'Schick sie uns',
    step2Body: 'Pack alles in ein Paket und leg deinen vorfrankierten Versandschein bei — den Versand übernehmen wir.',
    step3Title: '{rewardPoss|cap} erhalten',
    step3Body: {
      received: 'Sobald deine Teile bei uns angekommen und geprüft sind, schicken wir dir {rewardPoss} per E-Mail.',
      handin: 'Sobald dein Paket bei der Abgabe gescannt wird, schicken wir dir {rewardPoss} per E-Mail.',
      instant: '{rewardPoss|cap} kommt per E-Mail, sobald du bestätigst — danach schickst du uns deine Teile.',
    },
    cta: 'Alles klar',
    close: 'Schließen',
  },

  finder: {
    search: 'Katalog durchsuchen',
    photo: 'Per Foto hinzufügen',
    searchTitle: 'Katalog durchsuchen',
    searchSub: 'Nach SKU, EAN oder Produktname.',
    searchPlaceholder: 'Nach SKU, EAN oder Produktname suchen',
    carelabelHint: 'Tipp: SKU- oder Stilnummer findest du auf dem Pflegeetikett deines Artikels.',
    searching: 'Suche…',
    searchFailed: 'Suche fehlgeschlagen — versuche es erneut.',
    noMatches: 'Keine Treffer für „{query}“.',
    unavailable: 'Katalogsuche ist für diesen Shop nicht verfügbar.',
    estReward: 'Wert',
    notEligible: 'Nicht berechtigt',
    add: 'Hinzufügen',
    adding: 'Wird hinzugefügt…',
    added: '{name} hinzugefügt · +{amount}',
    backToOrders: 'Zurück zu deinen Bestellungen',
    showMore: 'Mehr Ergebnisse anzeigen',
    loadingMore: 'Wird geladen…',
  },

  photo: {
    dropTitle: 'Mach ein Foto deines Artikels',
    dropHint: 'Wir finden ähnliche Artikel in unserem Katalog.',
    or: 'oder',
    wayDevice: 'Foto aufnehmen oder hochladen',
    wayPhone: 'Kamera deines Handys nutzen',
    qrAgainTitle: 'Noch ein Foto versuchen',
    qrAgainHint: 'Mach ein weiteres Foto mit deinem Handy — wir suchen automatisch erneut.',
    analysingTitle: 'Deine besten Treffer werden gesucht…',
    analysingHint: 'Wir vergleichen dein Foto mit unserem Katalog',
    uploadingTitle: 'Dein Foto wird hochgeladen…',
    uploadingHint: 'Einen Moment',
    yourPhoto: 'Dein Foto',
    matchedTo: 'Mit unserem Katalog abgeglichen',
    replace: 'Ersetzen',
    headingConfident: 'Sieht einer davon wie dein Artikel aus?',
    headingCategory: 'Beste Treffer in {category}',
    noMatchHeading: 'Wir konnten keinen passenden Treffer finden',
    noMatchBody:
      'Keiner unserer Katalogartikel passte genau. Versuche ein anderes Foto oder suche stattdessen nach Namen.',
    // Distinct from "no match": the search never ran, so the item may well be in the catalogue.
    notSearched: 'Noch nicht gesucht',
    searchFailedHeading: 'Wir konnten gerade nicht suchen',
    searchFailedBody:
      'Bei uns ist etwas schiefgelaufen, daher haben wir dein Foto noch nicht verglichen. Dein Foto ist gespeichert — bitte versuche es erneut.',
    tryAgain: 'Erneut versuchen',
    refine: 'Nach Kategorie filtern',
    clear: 'Zurücksetzen',
    noneOfThese: 'Keiner davon',
    // „Nach Kategorie hinzufügen“ — Fallback über die Platzhalterkachel in den Foto-Ergebnissen.
    categoryCardTitle: 'Artikel nicht gefunden?',
    categoryCardHint: 'Wähle stattdessen eine Kategorie',
    categoryPickTitle: 'Um welche Art von Artikel handelt es sich?',
    categoryPickHint: 'Wir verwenden das gerade aufgenommene Foto.',
    categoryValue: 'Wert',
    categoryConfirm: 'Hinzufügen',
    categoryBack: 'Zurück',
    uploadAnother: 'Weiteres Foto hochladen',
    bestMatch: 'Bester Treffer',
    uploadFailed: 'Etwas ist schiefgelaufen — bitte versuche es erneut.',
  },

  match: {
    strong: 'Starker Treffer',
    colour: 'Farbe: {color}',
    estimatedReward: 'Wert',
    backToMatches: 'Zurück zu den Treffern',
    backToResults: 'Zurück zu den Ergebnissen',
    confirm: 'Ja, das ist mein Artikel',
  },

  details: {
    title: 'Deine Angaben',
    subtitle: 'Damit wir dein vorausbezahltes Versandetikett erstellen und deinen Wertgutschein senden können.',
    contact: 'Kontakt',
    address: 'Adresse',
    fullName: 'Vollständiger Name',
    email: 'E-Mail',
    country: 'Land',
    streetAddress: 'Straße und Hausnummer',
    postalCode: 'Postleitzahl',
    city: 'Stadt',
    prefix: 'Ländercode',
    phone: 'Telefon',
    emailLocked: 'Deine E-Mail ist mit deinem Konto verknüpft und kann hier nicht geändert werden.',
    consentPre: 'Ich akzeptiere die ',
    consentLink: 'Trade-in AGB',
    consentPost: ' und bestätige, dass die Artikel mir gehören.',
    required: 'Dieses Feld ist erforderlich.',
    emailInvalid: 'Gib eine gültige E-Mail-Adresse ein.',
    phoneInvalid: 'Gib eine gültige Telefonnummer ein.',
    zipInvalid: 'Gib eine gültige Postleitzahl ein',
    zipFormat: 'Format: {format}',
    loading: 'Deine Angaben werden geladen…',
    saveFailed: 'Wir konnten deine Angaben nicht speichern. Bitte versuche es erneut.',
  },

  terms: {
    title: 'Bedingungen für die Abgabe von {noun}',
    // {brand} ist der Markenname des Shops (config.brandName) oder ein generischer Fallback.
    introLead:
      'Im Rahmen unserer Secondhand-Initiative kannst du {nounPoss} von {brand} gegen einen Wertgutschein eintauschen.',
    introAccept:
      'Wenn du {nounPoss} von {brand} einsendest, akzeptierst du, dass ein Wertgutschein nur für {noun} ausgestellt wird, die die folgenden Bedingungen erfüllt, und dass {noun}, die die Anforderungen nicht erfüllt, NICHT an dich zurückgesendet wird.',
    introDonate:
      '{noun|cap}, die unsere Anforderungen nicht erfüllt, wird für wohltätige Zwecke gespendet oder an Unternehmen gegeben, die {nounDef} oder deren Materialien wiederverwenden können.',
    requirementsTitle: 'Anforderungen an {nounDef}, die du abgibst',
    requirementsBody:
      'Wir nehmen {noun} von {brand} an, die sauber ist und bei der alle Originaletiketten intakt sind – sowohl das Nackenetikett als auch das Pflegeetikett (Waschanleitung usw.).',
    howToTitle: 'So gibst du {nounPoss} ab',
    howToRegister:
      'Du registrierst die {brand}-Produkte, die du abgeben möchtest. Bei der Registrierung siehst du den Wert des jeweiligen Produkts – den Betrag, den du als Wertgutschein im Austausch für {nounPoss} erhältst.',
    howToAdjust:
      'Wenn das, was wir erhalten, nicht deiner Registrierung entspricht, korrigieren wir es auf das richtige Produkt und passen deinen Gesamtwert entsprechend an. Indem du {nounPoss} einsendest, akzeptierst du, dass wir diese Anpassung vornehmen.',
    howToLabel:
      'Wenn du deine Registrierung abgeschlossen hast, erhältst du ein Versandetikett oder einen QR-Code, mit dem du {nounPoss} einsendest. Dieses Versandetikett ist von {brand} im Voraus bezahlt.',
    howToFinal:
      'Sobald du {nounPoss} abgegeben hast, kannst du deine Einsendung nicht widerrufen, und wir senden deine Produkte nicht zurück.',
    voucherTitle: 'Wertgutschein',
    voucherIssue: {
      received:
        'Du erhältst einen Wertgutschein, sobald wir bestätigt haben, dass {nounDef} die oben genannten Anforderungen erfüllt. Der Wertgutschein wird an die angegebene E-Mail-Adresse gesendet.',
      handin:
        'Du erhältst einen Wertgutschein, sobald dein Paket an der Abgabestelle registriert wurde. Der Wertgutschein wird an die angegebene E-Mail-Adresse gesendet. Wenn {nounDef} die oben genannten Anforderungen nicht erfüllt, kann der Wertgutschein zurückgezogen werden.',
      instant:
        'Du erhältst einen Wertgutschein, sobald du deinen Trade-in registrierst. Der Wertgutschein wird an die angegebene E-Mail-Adresse gesendet. Wenn {nounDef} nicht an uns gesendet wird oder die oben genannten Anforderungen nicht erfüllt, kann der Wertgutschein zurückgezogen werden.',
    },
    voucherValidity:
      'Der Wertgutschein ist 3 Jahre ab Ausstellung gültig und kann für Einkäufe bei {brand} online verwendet werden. Der Wertgutschein kann nicht in Bargeld umgetauscht werden.',
    closing: 'Wir freuen uns darauf, {nounPoss} zu erhalten.',
    brandFallback: 'die Marke',
    close: 'Schließen',
    accept: 'Akzeptieren & fortfahren',
  },

  shipping: {
    title: 'Wie sendest du es ein?',
    subtitle: 'Packe deinen {count} Artikel — ein Etikett für alles.',
    subtitlePlural: 'Packe deine {count} Artikel — ein Etikett für alles.',
    loading: 'Versandoptionen werden geladen…',
    none: 'Für deine Adresse ist noch kein Versanddienst verfügbar.',
    maxExceeded: 'Dieser Trade-in überschreitet den Höchstwert. Entferne einen Artikel und versuche es erneut.',
    createFailed: 'Wir konnten deinen Trade-in nicht erstellen. Bitte versuche es erneut.',
    desc_gls: 'Abgabe in einem GLS PaketShop. Kostenloses, gedrucktes Etikett.',
    desc_dhl: 'Abgabe in einem DHL Paketshop. Kostenloses, gedrucktes Etikett.',
    desc_dao: 'Ein Kurier holt an deiner Tür ab.',
    desc_shipmondo: 'Abgabe mit einem gedruckten Etikett.',
    desc_pickup: 'Gib deine Artikel in unserem Geschäft ab.',
    desc_postnord: 'Abgabe an einer PostNord-Paketstation.',
  },

  story: {
    title: 'Erzähl uns deine Geschichte',
    subtitle: 'Optional — wir hören gern von deinen Stücken.',
    placeholder: 'Erzähl uns von diesem Stück — wo du es getragen hast, warum du es liebtest…',
    consent:
      'Ihr dürft meine Geschichte (anonym) teilen, um andere Kundinnen und Kunden zu inspirieren, ihrer Kleidung ein zweites Leben zu geben.',
  },

  confirm: {
    topbarTitle: 'Bestätigen',
    heroTitle: 'Alles erledigt',
    heroBody: {
      received:
        'Dein Trade-in ist registriert. Folge den drei Schritten unten, und dein Wertgutschein ist unterwegs.',
      handin:
        'Dein Trade-in ist registriert. Folge den drei Schritten unten — dein Wertgutschein wird ausgestellt, sobald du dein Paket abgibst.',
      instant:
        'Dein Trade-in ist registriert und dein Wertgutschein ist unterwegs. Folge einfach den drei Schritten unten, um uns deine Artikel zu senden.',
    },
    packTitle: 'Packe deinen {count} Artikel',
    packTitlePlural: 'Packe deine {count} Artikel',
    packBody: 'Lege alles in ein Paket. Keine spezielle Verpackung nötig — verwende einen Karton oder Beutel wieder.',
    pickupPackBody: 'Verpacke deine Artikel und bring sie in unser Geschäft — kein Versandetikett nötig.',
    shippingCode: 'Versandcode',
    generatingLabel: 'Dein Versandetikett wird erstellt…',
    labelFailed: 'Wir konnten dein Etikett nicht erstellen. Bitte kontaktiere den Support.',
    showQr: 'QR-Code anzeigen',
    showLabel: 'Versandetikett anzeigen',
    dropTitle: 'Bring es weg',
    dropBody: 'Zeige den Code oder das Etikett an der Abgabestelle deines Versanddienstes. ',
    findShop: 'Nächste Abgabestelle finden →',
    pickupTitle: 'Abgabe im Geschäft',
    pickupBody: 'Bring deine Artikel in unser Geschäft, den Rest übernehmen wir.',
    rewardTitle: 'Erhalte deinen Wertgutschein',
    rewardBodyPre: {
      received: 'Sobald wir dein Paket erhalten, senden wir dir deinen ',
      handin: 'Sobald dein Paket an der Abgabestelle gescannt wurde, senden wir dir deinen ',
      instant: 'Dein ',
    },
    rewardBodyPost: {
      received: ' Wertgutschein innerhalb von 2 Werktagen per E-Mail.',
      handin: ' Wertgutschein per E-Mail. Bitte gib uns ein paar Stunden, bis die Tracking-Daten der Abgabe bei uns eingehen.',
      instant: ' Wertgutschein ist bereits auf dem Weg in dein Postfach — sende uns einfach deine Artikel über die Schritte oben.',
    },
    // INSTANT-Strategie: der Wertgutschein-Code wird hier angezeigt, sobald Core ihn ausgestellt hat.
    voucherReadyTitle: 'Dein Wertgutschein ist bereit',
    voucherIssuing: 'Dein Wertgutschein wird erstellt…',
    voucherExpires: 'gültig bis {date}',
    copyCode: 'Wertgutschein-Code kopieren',
    codeCopied: 'Wertgutschein-Code kopiert',
    startAnother: 'Neuen Trade-in starten',
    backToShop: 'Zurück zum Shop',
  },

  dropoff: {
    title: 'Nächste Abgabestellen',
    qrSupported: 'Unterstützt QR-Abgabe — kein gedrucktes Etikett nötig.',
    qrNotSupported: 'Erfordert ein gedrucktes Etikett.',
  },
}
